import { useEffect, useState } from "react";
import { Users, CheckCircle2, Eye, RefreshCw, Clock, Building2, User, Shield } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const ROLE_META = {
  landlord: { label: "Investor", icon: Building2, tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  tenant: { label: "Tenant", icon: User, tone: "bg-sky-50 text-sky-700 border-sky-200" },
  admin: { label: "Admin", icon: Shield, tone: "bg-violet-50 text-violet-700 border-violet-200" },
};

function relativeTime(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function hoursUntil(iso) {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.round(diff / 3600000));
}

/**
 * Admin-only widget: last 20 accounts the current admin has onboarded in
 * the past 72h. Each row exposes a "Reveal password" button that fetches
 * the decrypted temp password on demand and copies it to the clipboard.
 * Mongo auto-purges the underlying collection via TTL index after 72h.
 */
export default function AdminRecentAccounts({ refreshSignal }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [revealing, setRevealing] = useState(null); // id of row whose password is being fetched
  const [justCopied, setJustCopied] = useState(null); // id of row that was just copied

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/admin/recent-created-accounts");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [refreshSignal]);

  const reveal = async (row) => {
    setRevealing(row.id);
    try {
      const { data } = await api.get(`/admin/recent-created-accounts/${row.id}/password`);
      try {
        await navigator.clipboard.writeText(data.temp_password);
        setJustCopied(row.id);
        toast.success(`Password copied · ${row.user_email}`);
        setTimeout(() => setJustCopied(null), 2500);
      } catch {
        // Clipboard blocked — show it in a toast so admin can copy manually
        toast.message(`Temp password for ${row.user_email}`, {
          description: data.temp_password,
          duration: 10000,
        });
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Reveal failed");
    } finally {
      setRevealing(null);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid="admin-recent-accounts">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-slate-500" />
          <div>
            <div className="overline text-slate-500">Created by you</div>
            <h3 className="text-sm font-bold text-slate-900">Recent accounts</h3>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 inline-flex items-center gap-1">
            <Clock className="w-3 h-3" /> 72h window
          </span>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="text-slate-500 hover:text-slate-900 p-1 rounded-sm"
            title="Refresh"
            data-testid="admin-recent-accounts-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {error ? (
        <div className="px-5 py-6 text-sm text-red-600" data-testid="admin-recent-accounts-error">{error}</div>
      ) : loading && rows.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-slate-400" data-testid="admin-recent-accounts-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="px-5 py-10 text-center" data-testid="admin-recent-accounts-empty">
          <div className="text-sm text-slate-600">No recent accounts</div>
          <div className="text-xs text-slate-400 mt-1">Accounts you onboard via <em>Create new account</em> appear here for 72 hours.</div>
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {rows.map((r) => {
            const meta = ROLE_META[r.user_role] || ROLE_META.landlord;
            const Icon = meta.icon;
            const hrs = hoursUntil(r.expires_at);
            const copied = justCopied === r.id;
            return (
              <li key={r.id} className="px-5 py-3 flex items-center justify-between gap-3 hover:bg-slate-50 transition" data-testid={`admin-recent-row-${r.id}`}>
                <div className="min-w-0 flex items-start gap-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${meta.tone}`}>
                    <Icon className="w-3 h-3" /> {meta.label}
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{r.user_full_name}</div>
                    <div className="text-xs text-slate-500 truncate font-mono">{r.user_email}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mt-0.5">
                      {relativeTime(r.created_at)} · expires in {hrs}h
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => reveal(r)}
                  disabled={revealing === r.id}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border text-xs font-mono uppercase tracking-widest transition ${copied ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-700 hover:border-slate-900 hover:bg-slate-900 hover:text-white"}`}
                  data-testid={`admin-recent-reveal-${r.id}`}
                >
                  {copied ? (
                    <>
                      <CheckCircle2 className="w-3.5 h-3.5" /> Copied
                    </>
                  ) : revealing === r.id ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Revealing…
                    </>
                  ) : (
                    <>
                      <Eye className="w-3.5 h-3.5" /> Copy password
                    </>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
